import _ from 'underscore';

import {BatchSchema} from 'constants/SampleConstants';
import * as Autobatcher from 'models/Autobatcher';
import {getModelManager, ManagedModelType} from 'models/ManagedModelDefinitions';
import {SearchView} from 'nav/sidepanel/listing/search-results/SearchResultsView';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {composeSidepanelSearchStartedEvent} from 'performance/processSearchEvents';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch, Thunk} from 'redux-stores/Store';
import {Schema} from 'schemas/SchemaConstants';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';

import {SearchUISelector} from './reduceSearchUI';
import {
  AsyncFetcherLocation,
  Filter,
  FilterErrors,
  GroupResult,
  SearchNextToken,
  SearchParams,
  SearchType,
  Sort,
} from './SearchConstants';
import SearchResultsModelManager from './SearchResultsModelManager';

export enum SearchUIActionType {
  SEARCH_FORCE_REFRESHED = 'SEARCH_FORCE_REFRESHED',
  SEARCH_PARAMS_UPDATED = 'SEARCH_PARAMS_UPDATED',
  SEARCH_STARTED = 'SEARCH_STARTED',
  EXPANDED_IDS_UPDATED = 'EXPANDED_IDS_UPDATED',
  CHILD_FETCH_ID_ADDED = 'CHILD_FETCH_ID_ADDED',
  CHILD_FETCH_ID_REMOVED = 'CHILD_FETCH_ID_REMOVED',
  SELECTED_IDS_UPDATED = 'SELECTED_IDS_UPDATED',
  UPDATE_FILTERS = 'UPDATE_FILTERS',
  PAGE_CHANGED = 'PAGE_CHANGED',
  UPDATE_QUERY_STRING = 'UPDATE_QUERY_STRING',
  UPDATE_SORT_ORDER = 'UPDATE_SORT_ORDER',
  UPDATE_PAGE_SIZE = 'UPDATE_PAGE_SIZE',
  UPDATE_GROUP_BY = 'UPDATE_GROUP_BY',
  NEXT_TOKEN_ADDED = 'NEXT_TOKEN_ADDED',
  UPDATE_ACTIVE_SAVED_SEARCH = 'UPDATE_ACTIVE_SAVED_SEARCH',
  UPDATE_VIEW_SELECTION = 'UPDATE_VIEW_SELECTION',
  UPDATE_GROUP_BY_SELECTION = 'UPDATE_GROUP_BY_SELECTION',
  UPDATE_ASYNC_FETCHER_IS_FETCHED_STATE_ACTION = 'UPDATE_ASYNC_FETCHER_IS_FETCHED_STATE_ACTION',
  UPDATE_MULTIQUERY_BLAST_OFFSET = 'UPDATE_MULTIQUERY_BLAST_OFFSET',
  UPDATE_REQUEST_CONSISTENCY = 'UPDATE_REQUEST_CONSISTENCY',
}

interface BaseSearchParamAction {
  searchSessionId: string;
  searchType: SearchType;
}

interface UpdateFiltersAction extends BaseSearchParamAction {
  type: SearchUIActionType.UPDATE_FILTERS;
  filters: Array<Filter>;
  selectedSchema: Schema | BatchSchema | null;
}
export function updateFilters(
  searchSessionId: string,
  searchType: SearchType,
  filters: Array<Filter>,
  selectedSchema: Schema | BatchSchema | null
): UpdateFiltersAction {
  return {
    type: SearchUIActionType.UPDATE_FILTERS,
    searchSessionId,
    searchType,
    filters,
    selectedSchema,
  };
}

interface PageChangedAction extends BaseSearchParamAction {
  type: SearchUIActionType.PAGE_CHANGED;
  offset: number;
}
export function pageChanged(
  searchSessionId: string,
  searchType: SearchType,
  offset: number
): PageChangedAction {
  return {
    type: SearchUIActionType.PAGE_CHANGED,
    searchSessionId,
    searchType,
    offset,
  };
}

interface UpdateQueryStringAction extends BaseSearchParamAction {
  type: SearchUIActionType.UPDATE_QUERY_STRING;
  query: string;
}
export function updateQueryString(
  searchSessionId: string,
  searchType: SearchType,
  query: string
): UpdateQueryStringAction {
  return {
    type: SearchUIActionType.UPDATE_QUERY_STRING,
    searchSessionId,
    searchType,
    query,
  };
}

interface UpdateSortOrderAction extends BaseSearchParamAction {
  type: SearchUIActionType.UPDATE_SORT_ORDER;
  sorts: Array<Sort>;
}
export function updateSortOrder(
  searchSessionId: string,
  searchType: SearchType,
  sorts: Array<Sort>
): UpdateSortOrderAction {
  return {
    type: SearchUIActionType.UPDATE_SORT_ORDER,
    searchSessionId,
    searchType,
    sorts,
  };
}

interface UpdatePageSizeAction extends BaseSearchParamAction {
  type: SearchUIActionType.UPDATE_PAGE_SIZE;
  limit: number;
}
export function updatePageSize(
  searchSessionId: string,
  searchType: SearchType,
  limit: number
): UpdatePageSizeAction {
  return {
    type: SearchUIActionType.UPDATE_PAGE_SIZE,
    searchSessionId,
    searchType,
    limit,
  };
}

interface UpdateGroupByAction extends BaseSearchParamAction {
  type: SearchUIActionType.UPDATE_GROUP_BY;
  groupField: string | null;
}
export function updateGroupBy(
  searchSessionId: string,
  searchType: SearchType,
  groupField: string | null
): UpdateGroupByAction {
  return {
    type: SearchUIActionType.UPDATE_GROUP_BY,
    searchSessionId,
    searchType,
    groupField,
  };
}

interface UpdateSearchUIAction {
  type: SearchUIActionType.SEARCH_PARAMS_UPDATED;
  searchType: SearchType;
  searchSessionId: string;
  searchParams: SearchParams;
  filterErrors: FilterErrors;
}
export function updateSearchParams(
  searchSessionId: string,
  searchType: SearchType,
  searchParams: SearchParams,
  filterErrors: FilterErrors
): UpdateSearchUIAction {
  return {
    type: SearchUIActionType.SEARCH_PARAMS_UPDATED,
    searchSessionId,
    searchParams,
    filterErrors,
    searchType,
  };
}

interface StartSearchAction {
  type: SearchUIActionType.SEARCH_STARTED;
  searchSessionId: string;
  searchId: string;
  searchType: SearchType;
  searchParams: SearchParams;
  filterErrors: FilterErrors;
}
export function startSearch(
  searchSessionId: string,
  searchType: SearchType,
  searchParams: SearchParams,
  filterErrors: FilterErrors,
  searchId: string | null = null
): StartSearchAction {
  searchId = searchId || _.uniqueId('search_');
  logProfilerTimingEvent(composeSidepanelSearchStartedEvent(searchId));
  return {
    type: SearchUIActionType.SEARCH_STARTED,
    searchSessionId,
    searchId,
    searchType,
    filterErrors,
    searchParams,
  };
}

export function updateParamsAndSearch(
  searchSessionId: string,
  searchType: SearchType,
  searchParams: SearchParams,
  filterErrors: FilterErrors
): Thunk<void> {
  return (dispatch) => {
    dispatch(updateSearchParams(searchSessionId, searchType, searchParams, filterErrors));
    dispatch(startSearch(searchSessionId, searchType, searchParams, filterErrors));
  };
}

function _refreshListing(state: BenchlingState, searchSessionId: string, dispatch: BenchlingDispatch): void {
  const selector = new SearchUISelector(state);
  const searchId = selector.getSearchIdForSearchSession(searchSessionId);

  // Listing refreshes are triggered after certain object mutations (e.g. create, star/unstar), so we should
  // explicitly request consistency to ensure the search index reflects the latest changes before querying.
  // The flag is reset after the search completes in SearchResultsModelManager.
  dispatch(updateRequestConsistency(searchId, true));

  // We use forceFetch so we that we can show the old results in a stale state while a new search is run.
  Autobatcher.requestFetch(getModelManager(ManagedModelType.SEARCH_RESULTS), searchId, {forceFetch: true});
  // We have to refetch various async fetchers to fill out some columns with fresh data
  // We don't have to reset the SidepanelItemList one because you only can refresh from the expanded search view
  dispatch(
    updateAsyncFetcherIsFetchedState(searchId, AsyncFetcherLocation.ITEM_LISTING_ENHANCE_PROPS, false)
  );
  dispatch(updateAsyncFetcherIsFetchedState(searchId, AsyncFetcherLocation.EXPANDED_ITEM_TABLE_GQL, false));
  dispatch(
    updateAsyncFetcherIsFetchedState(searchId, AsyncFetcherLocation.EXPANDED_ITEM_TABLE_ENHANCE_PROPS, false)
  );
}

export function refreshListing(searchSessionId: string): Thunk<void> {
  return (dispatch, getState) => {
    _refreshListing(getState(), searchSessionId, dispatch);
  };
}

export function maybeRefreshListing(searchSessionId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const selector = new SearchUISelector(state);
    if (selector.getSearchSessionState(searchSessionId) == null) {
      return;
    }
    _refreshListing(state, searchSessionId, dispatch);
  };
}

interface ForceRefreshSearchAction {
  type: SearchUIActionType.SEARCH_FORCE_REFRESHED;
  searchSessionId: string;
  searchId: string;
  clearSelection: boolean;
}

export function forceRefreshSearch(
  searchSessionId: string,
  options: {clearSelection: boolean} = {clearSelection: false}
): ForceRefreshSearchAction {
  const searchId = _.uniqueId('search_');
  return {
    type: SearchUIActionType.SEARCH_FORCE_REFRESHED,
    searchSessionId,
    searchId,
    clearSelection: options.clearSelection,
  };
}

interface UpdateActiveSavedSearchAction {
  type: SearchUIActionType.UPDATE_ACTIVE_SAVED_SEARCH;
  activeSavedSearch: SavedSearch | undefined;
  searchSessionId: string;
}

export function setSelectedSavedSearch(
  activeSavedSearch: SavedSearch | undefined,
  searchSessionId: string
): UpdateActiveSavedSearchAction {
  return {type: SearchUIActionType.UPDATE_ACTIVE_SAVED_SEARCH, activeSavedSearch, searchSessionId};
}

interface UpdateExpandedIdsAction {
  type: SearchUIActionType.EXPANDED_IDS_UPDATED;
  searchId: string;
  expandedIds: Array<string>;
}

/**
 * Hide or show an item's children, fetching the first page if necessary.
 */
export function setItemsExpanded(searchId: string, itemIds: Array<string>, expanded: boolean): Thunk<void> {
  return (dispatch, getState) => {
    const searchState = new SearchUISelector(getState());
    const prevExpandedIds = new Set(searchState.getExpandedIdsForSearch(searchId));
    const idsToChange = new Set(itemIds);
    const newExpandedIds = expanded
      ? new Set([...prevExpandedIds, ...idsToChange]) // Set union
      : [...prevExpandedIds].filter((id) => !idsToChange.has(id)); // Set difference
    dispatch({type: SearchUIActionType.EXPANDED_IDS_UPDATED, searchId, expandedIds: [...newExpandedIds]});

    if (expanded) {
      // Skip making a network request for any group which doesn't have any children to load, or already has
      // some children loaded (we'll just display those children).
      const groupIdsToFetch = itemIds.filter((id) => {
        const group = getGroupResult(searchId, id);
        return group.count > 0 && group.childIds.length === 0;
      });
      const modelManager = getModelManager(ManagedModelType.SEARCH_RESULTS) as SearchResultsModelManager;
      const limit = searchState.getChildrenPageSizeForSearch(searchId, groupIdsToFetch.length > 1);
      dispatch(modelManager.getNextGroupChildren(searchId, groupIdsToFetch, limit));
    }
  };
}

interface AddChildrenLoadingIdsAction {
  type: SearchUIActionType.CHILD_FETCH_ID_ADDED;
  searchId: string;
  groupIds: Array<string>;
}

export function addChildrenLoadingIds(
  searchId: string,
  groupIds: Array<string>
): AddChildrenLoadingIdsAction {
  return {type: SearchUIActionType.CHILD_FETCH_ID_ADDED, searchId, groupIds};
}

interface UpdateViewSelectionAction {
  type: SearchUIActionType.UPDATE_VIEW_SELECTION;
  searchId: string;
  viewSelection: SearchView;
}

export function updateViewSelection(searchId: string, viewSelection: SearchView): UpdateViewSelectionAction {
  return {type: SearchUIActionType.UPDATE_VIEW_SELECTION, searchId, viewSelection};
}

interface UpdateRequestConsistencyAction {
  type: SearchUIActionType.UPDATE_REQUEST_CONSISTENCY;
  searchId: string;
  requestConsistency: boolean | undefined;
}

export function updateRequestConsistency(
  searchId: string,
  requestConsistency: boolean | undefined
): UpdateRequestConsistencyAction {
  return {type: SearchUIActionType.UPDATE_REQUEST_CONSISTENCY, searchId, requestConsistency};
}

interface UpdateAsyncFetcherIsFetchedStateAction {
  type: SearchUIActionType.UPDATE_ASYNC_FETCHER_IS_FETCHED_STATE_ACTION;
  searchId: string;
  flagName: AsyncFetcherLocation;
  value: boolean;
}

// This should never be updated to return a Thunk because we need this to be handled synchronously
// Since the pattern of this action is setting it to true and fetching the relevant fetchers when it is false
// if it becomes async then we'll end up fetching the data multiple times.
export function updateAsyncFetcherIsFetchedState(
  searchId: string,
  flagName: AsyncFetcherLocation,
  value: boolean
): UpdateAsyncFetcherIsFetchedStateAction {
  return {type: SearchUIActionType.UPDATE_ASYNC_FETCHER_IS_FETCHED_STATE_ACTION, searchId, flagName, value};
}

interface UpdateGroupBySelectionAction {
  type: SearchUIActionType.UPDATE_GROUP_BY_SELECTION;
  searchId: string;
  groupBySelection: string | undefined;
}

export function updateGroupBySelection(
  searchId: string,
  groupBySelection: string | undefined
): UpdateGroupBySelectionAction {
  return {type: SearchUIActionType.UPDATE_GROUP_BY_SELECTION, searchId, groupBySelection};
}

interface UpdateMultiqueryBlastOffsetAction {
  type: SearchUIActionType.UPDATE_MULTIQUERY_BLAST_OFFSET;
  searchId: string;
  multiqueryBlastOffset: {[groupId: string]: number};
}

export function updateMultiqueryBlastOffset(
  searchId: string,
  multiqueryBlastOffset: {[groupId: string]: number}
): UpdateMultiqueryBlastOffsetAction {
  return {type: SearchUIActionType.UPDATE_MULTIQUERY_BLAST_OFFSET, searchId, multiqueryBlastOffset};
}

/**
 * Fetch the next page of children for the given item.
 */
export function loadMoreChildItems(searchId: string, parentItemId: string): Thunk<void> {
  return (dispatch, getState) => {
    // TODO(Search): add state so that the load button gets disabled while it's loading
    const group = getGroupResult(searchId, parentItemId);
    if (group.count !== group.childIds.length) {
      const modelManager = getModelManager(ManagedModelType.SEARCH_RESULTS) as SearchResultsModelManager;
      const searchState = new SearchUISelector(getState());

      dispatch(
        modelManager.getNextGroupChildren(
          searchId,
          [parentItemId],
          searchState.getChildrenPageSizeForSearch(searchId, false)
        )
      );
    }
  };
}

interface RemoveChildrenLoadingIdsAction {
  type: SearchUIActionType.CHILD_FETCH_ID_REMOVED;
  searchId: string;
  groupIds: Array<string>;
}

export function removeChildrenLoadingIds(
  searchId: string,
  groupIds: Array<string>
): RemoveChildrenLoadingIdsAction {
  return {type: SearchUIActionType.CHILD_FETCH_ID_REMOVED, searchId, groupIds};
}

function getGroupResult(searchId: string, parentItemId: string): GroupResult {
  const modelManager = getModelManager(ManagedModelType.SEARCH_RESULTS) as SearchResultsModelManager;
  const searchResults = modelManager.getModel(searchId);
  if (!searchResults || !searchResults.groups) {
    throw new Error('Expected to have search results with groups.');
  }
  const group = searchResults.groups.find((g) => g.key === parentItemId);
  if (!group) {
    throw new Error('Did not find group with specified key in searchResults.');
  }
  return group;
}

interface UpdateSelectedIdsAction {
  type: SearchUIActionType.SELECTED_IDS_UPDATED;
  searchId: string;
  selectedIds: Array<string>;
}

function updateSelectedIds(searchId: string, selectedIds: Array<string>): UpdateSelectedIdsAction {
  return {
    type: SearchUIActionType.SELECTED_IDS_UPDATED,
    searchId,
    selectedIds,
  };
}

export function selectIds(searchId: string, selectedIds: Array<string>): Thunk<void> {
  return (dispatch) => {
    dispatch(updateSelectedIds(searchId, selectedIds));
  };
}

interface NextTokenAddedAction {
  type: SearchUIActionType.NEXT_TOKEN_ADDED;
  // We store next tokens with the search session, but have to dispatch by search ID for two reasons:
  //  1. The next token is retrieved from SearchResultsModelManager, which doesn't know about session ids.
  //  2. We need to confirm that the searchId is still the active search for the session in the reducer
  //      so that we don't add a next token to a session that is actually from a stale search.
  searchId: string;
  nextToken: SearchNextToken;
}
export function addNextToken(searchId: string, nextToken: SearchNextToken): NextTokenAddedAction {
  return {
    type: SearchUIActionType.NEXT_TOKEN_ADDED,
    searchId,
    nextToken,
  };
}

export type SearchUIAction =
  | UpdateSearchUIAction
  | StartSearchAction
  | UpdateExpandedIdsAction
  | UpdateSelectedIdsAction
  | ForceRefreshSearchAction
  | UpdateFiltersAction
  | PageChangedAction
  | UpdateQueryStringAction
  | UpdateSortOrderAction
  | UpdatePageSizeAction
  | UpdateGroupByAction
  | UpdateActiveSavedSearchAction
  | NextTokenAddedAction
  | AddChildrenLoadingIdsAction
  | RemoveChildrenLoadingIdsAction
  | UpdateViewSelectionAction
  | UpdateGroupBySelectionAction
  | UpdateRequestConsistencyAction
  | UpdateAsyncFetcherIsFetchedStateAction
  | UpdateMultiqueryBlastOffsetAction;
